import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../components/AuthProvider";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Eye, EyeOff, AlertCircle, CheckCircle, Mail } from "lucide-react";

import { toast } from "sonner";
import { api } from "../lib/utils";

export default function LoginPage() {
  const [activeTab, setActiveTab] = useState("login");
  
  // Login state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  
  // Signup state
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupConfirmPassword, setSignupConfirmPassword] = useState("");
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  
  // Forgot password state
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  
  // Resend verification state
  const [resendEmail, setResendEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  
  const { login, user } = useAuth();
  const navigate = useNavigate();

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoginLoading(true);

    try {
      await login(loginEmail, loginPassword);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (err) {
      const message = err.response?.data?.detail || "Invalid email or password";
      setLoginError(message);
      toast.error(message);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    
    if (signupPassword !== signupConfirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (signupPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setSignupLoading(true);
    
    try {
      await api.post(`/api/auth/signup`, {
        email: signupEmail,
        password: signupPassword
      });
      setSignupSuccess(true);
      toast.success("Account created! Check your email to verify.");
    } catch (err) {
      const message = err.response?.data?.detail || "Failed to create account";
      toast.error(message);
    } finally {
      setSignupLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    
    try {
      await api.post(`/api/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
      toast.success("If this email exists, a reset link has been sent.");
    } catch (err) {
      toast.error("Failed to send reset email");
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResendVerification = async (e) => {
    e.preventDefault();
    setResendLoading(true);
    
    try {
      await api.post(`/api/auth/resend-verification`, { email: resendEmail });
      setResendSent(true);
      toast.success("If this email is registered, a verification link has been sent.");
    } catch (err) {
      toast.error("Failed to send verification email");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://customer-assets.emergentagent.com/job_flight-data-hub-2/artifacts/csnl60lr_ChatGPT%20Image%20Mar%2012%2C%202026%2C%2012_02_41%20AM.png"
          alt="Flight operations center"
          className="object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent" />
        <div className="absolute bottom-16 left-16 right-16">
          <h2 className="font-heading text-3xl font-semibold text-white mb-1 tracking-wide">
            Cuvano® FlightCore
          </h2>
          <p className="text-zinc-300 text-base font-light tracking-wide">
            From Sky to Insight.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          {/* Logo for mobile */}
          <div className="lg:hidden flex flex-col items-center mb-8">
            <span className="font-heading font-semibold text-xl tracking-wide">
              Cuvano® FlightCore
            </span>
            <p className="text-muted-foreground text-sm font-light mt-1">From Sky to Insight.</p>
          </div>

          {/* Auth Card */}
          <div className="backdrop-blur-md bg-card/50 border border-border rounded-lg p-8 shadow-sm">
            <div className="hidden lg:flex items-center justify-center mb-6">
              <h2 className="font-heading font-semibold text-lg tracking-wide">
                Welcome to Cuvano® FlightCore
              </h2>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login" data-testid="tab-login" className="text-sm">Sign In</TabsTrigger>
                <TabsTrigger value="signup" data-testid="tab-signup" className="text-sm">Create Account</TabsTrigger>
              </TabsList>

              {/* Login Tab */}
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="login-email" className="text-sm font-medium">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="you@example.com"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      required
                      autoComplete="email"
                      data-testid="login-email"
                      className="bg-background h-10 text-sm"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="login-password" className="text-sm font-medium">Password</Label>
                    <div className="relative">
                      <Input
                        id="login-password"
                        type={showLoginPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        required
                        autoComplete="current-password"
                        data-testid="login-password"
                        className="bg-background pr-10 h-10 text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => setShowLoginPassword(!showLoginPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showLoginPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  {loginError && (
                    <div className="flex items-center gap-2 text-destructive text-sm bg-destructive/10 p-3 rounded-md">
                      <AlertCircle className="h-4 w-4 flex-shrink-0" />
                      <span className="text-sm">{loginError}</span>
                    </div>
                  )}

                  <Button type="submit" className="w-full h-10 text-sm font-medium" disabled={loginLoading} data-testid="login-submit">
                    {loginLoading ? "Signing in..." : "Sign In"}
                  </Button>

                  <div className="flex flex-col gap-1.5 pt-2">
                    <button
                      type="button"
                      onClick={() => setActiveTab("forgot")}
                      className="text-sm text-primary hover:underline text-center"
                      data-testid="forgot-password-link"
                    >
                      Forgot your password?
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab("resend")}
                      className="text-xs text-muted-foreground hover:text-primary text-center"
                      data-testid="resend-verification-link"
                    >
                      Resend verification email
                    </button>
                  </div>
                </form>
              </TabsContent>

              {/* Signup Tab */}
              <TabsContent value="signup">
                {signupSuccess ? (
                  <div className="text-center py-6">
                    <CheckCircle className="h-10 w-10 text-green-500 mx-auto mb-4" />
                    <h3 className="font-medium text-base mb-2">Check your email</h3>
                    <p className="text-muted-foreground text-sm mb-2">
                      We've sent a verification link to <strong>{signupEmail}</strong>
                    </p>
                    <p className="text-muted-foreground text-xs mb-4">
                      After verification, an admin will review and approve your access.
                    </p>
                    <Button variant="outline" size="sm" onClick={() => { setSignupSuccess(false); setActiveTab("login"); }}>
                      Back to Sign In
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSignup} className="space-y-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="signup-email" className="text-sm font-medium">Email</Label>
                      <Input
                        id="signup-email"
                        type="email"
                        placeholder="you@example.com"
                        value={signupEmail}
                        onChange={(e) => setSignupEmail(e.target.value)}
                        required
                        data-testid="signup-email"
                        className="bg-background h-10 text-sm"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="signup-password" className="text-sm font-medium">Password</Label>
                      <div className="relative">
                        <Input
                          id="signup-password"
                          type={showSignupPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={signupPassword}
                          onChange={(e) => setSignupPassword(e.target.value)}
                          required
                          minLength={6}
                          data-testid="signup-password"
                          className="bg-background pr-10 h-10 text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSignupPassword(!showSignupPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showSignupPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="signup-confirm" className="text-sm font-medium">Confirm Password</Label>
                      <Input
                        id="signup-confirm"
                        type="password"
                        placeholder="••••••••"
                        value={signupConfirmPassword}
                        onChange={(e) => setSignupConfirmPassword(e.target.value)}
                        required
                        data-testid="signup-confirm-password"
                        className="bg-background h-10 text-sm"
                      />
                    </div>

                    <Button type="submit" className="w-full h-10 text-sm font-medium" disabled={signupLoading} data-testid="signup-submit">
                      {signupLoading ? "Creating account..." : "Create Account"}
                    </Button>

                    <p className="text-xs text-muted-foreground text-center">
                      By signing up, you agree to receive verification emails.
                    </p>
                  </form>
                )}
              </TabsContent>

              {/* Forgot Password */}
              <TabsContent value="forgot">
                {forgotSent ? (
                  <div className="text-center py-6">
                    <Mail className="h-10 w-10 text-primary mx-auto mb-4" />
                    <h3 className="font-medium text-base mb-2">Check your email</h3>
                    <p className="text-muted-foreground text-sm mb-4">
                      If an account exists for <strong>{forgotEmail}</strong>, we've sent password reset instructions.
                    </p>
                    <Button variant="outline" size="sm" onClick={() => { setForgotSent(false); setActiveTab("login"); }}>
                      Back to Sign In
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div className="text-center mb-4">
                      <h3 className="font-medium text-base">Forgot Password</h3>
                      <p className="text-muted-foreground text-sm">
                        Enter your email to receive reset instructions.
                      </p>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="forgot-email" className="text-sm font-medium">Email</Label>
                      <Input
                        id="forgot-email"
                        type="email"
                        placeholder="you@example.com"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        required
                        data-testid="forgot-email"
                        className="bg-background h-10 text-sm"
                      />
                    </div>

                    <Button type="submit" className="w-full h-10 text-sm font-medium" disabled={forgotLoading} data-testid="forgot-submit">
                      {forgotLoading ? "Sending..." : "Send Reset Link"}
                    </Button>

                    <button
                      type="button"
                      onClick={() => setActiveTab("login")}
                      className="text-sm text-muted-foreground hover:text-primary w-full text-center"
                    >
                      Back to Sign In
                    </button>
                  </form>
                )}
              </TabsContent>

              {/* Resend Verification */}
              <TabsContent value="resend">
                {resendSent ? (
                  <div className="text-center py-6">
                    <Mail className="h-10 w-10 text-primary mx-auto mb-4" />
                    <h3 className="font-medium text-base mb-2">Check your email</h3>
                    <p className="text-muted-foreground text-sm mb-4">
                      If your account needs verification, we've sent a new link to <strong>{resendEmail}</strong>
                    </p>
                    <Button variant="outline" size="sm" onClick={() => { setResendSent(false); setActiveTab("login"); }}>
                      Back to Sign In
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleResendVerification} className="space-y-4">
                    <div className="text-center mb-4">
                      <h3 className="font-medium text-base">Resend Verification</h3>
                      <p className="text-muted-foreground text-sm">
                        Enter your email to receive a new verification link.
                      </p>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="resend-email" className="text-sm font-medium">Email</Label>
                      <Input
                        id="resend-email"
                        type="email"
                        placeholder="you@example.com"
                        value={resendEmail}
                        onChange={(e) => setResendEmail(e.target.value)}
                        required
                        data-testid="resend-email"
                        className="bg-background h-10 text-sm"
                      />
                    </div>

                    <Button type="submit" className="w-full h-10 text-sm font-medium" disabled={resendLoading} data-testid="resend-submit">
                      {resendLoading ? "Sending..." : "Resend Verification"}
                    </Button>

                    <button
                      type="button"
                      onClick={() => setActiveTab("login")}
                      className="text-sm text-muted-foreground hover:text-primary w-full text-center"
                    >
                      Back to Sign In
                    </button>
                  </form>
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* Footer */}
          <p className="text-zinc-500 text-xs tracking-wider text-center mt-8">
            POWERED BY CUVANO®
          </p>
        </div>
      </div>
    </div>
  );
}
